<script lang="ts" setup>
import { useRoute } from "vue-router";

import { useAuthStore } from "@/store/auth/index.ts";
import { usePrintersStore } from "@/store/printers/index.ts";
import { getRoleLabel } from "@/utils/options.js";

import CommonLettersAvatar from "@/components/common/CommonLettersAvatar.vue";
import CommonSimpleMenu from "@/components/common/CommonSimpleMenu.vue";
import CommonSkeleton from "@/components/common/CommonSkeleton.vue";
import UserMenu from "@/components/core/AppHeader/UserMenu.vue";

const props = defineProps<{
  loading: boolean
}>();
const route = useRoute();
const authStore = useAuthStore();
const printersStore = usePrintersStore();
</script>

<template>
  <header
    class="h-[100px] flex justify-center items-center p-4 pl-[30px] transition-all duration-500"
  >
    <section class="app-header">
      <div class="flex items-center justify-center p-3 gap-4">
        <slot name="left" />
        <h2 class="page-block-title">
          {{ route.meta.pageTitle || route.meta.title }}
        </h2>
      </div>
      <section
        v-if="!props.loading"
        class="flex gap-3 px-5"
      >
        <slot />
        <div
          v-if="route.meta?.printersList"
          class="flex justify-center items-center"
        >
          <feather-icon
            class="cursor-pointer hover:text-primary duration-500 transition-all"
            size="30"
            tabindex="0"
            type="printer"
            @click="printersStore.openPrinterSideBar(true)"
            @keydown.enter="printersStore.openPrinterSideBar(true)"
            @keydown.space.prevent="printersStore.openPrinterSideBar(true)"
          />
        </div>
        <div class="flex w-full gap-2 tour-user-menu">
          <div
            class="hidden md:flex flex-col justify-center gap-1 w-full text-right whitespace-nowrap"
          >
            <span class="text-mainText text-[16px]">
              {{ authStore.getUser?.name }}
            </span>
            <span class="text-secondaryText font-extralight text-[14px]">
              {{ getRoleLabel(authStore.getUser?.role) }}</span>
          </div>
          <CommonSimpleMenu width="150px">
            <template #button>
              <CommonLettersAvatar
                :name="authStore.getUser?.name"
                online-dot
              />
            </template>
            <template #items>
              <UserMenu />
            </template>
          </CommonSimpleMenu>
        </div>
      </section>
      <section
        v-else
        class="flex gap-2 items-center px-3"
      >
        <div class="hidden md:flex flex-col items-end gap-2">
          <CommonSkeleton
            height="15px"
            width="150px"
          />
          <CommonSkeleton
            height="15px"
            width="100px"
          />
        </div>
        <div>
          <CommonSkeleton
            border-radius="100%"
            height="50px"
            width="50px"
          />
        </div>
      </section>
    </section>
  </header>
</template>

<style lang="scss" scoped>
.app-header {
  @apply flex justify-between shadow-md dark:shadow-slate-900/50 w-full rounded-lg h-[70px];
  .app-header-logo {
    @apply hidden md:flex justify-center items-center;
  }
}
</style>
